// src/sot/primitives/Sheet.jsx
import React from "react";
import { createPortal } from "react-dom";
import { shadow } from "@/sot/tokens/shadow";

export default function Sheet({ open, onClose, children, presentation = "sheet", footer = null, ariaLabel = "Dialog", ariaLabelledBy }) {
  const isOpen = Boolean(open);
  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  function onBackdropPointerDown(e) {
    if (e.target !== e.currentTarget) return;
    onClose?.();
  }

  if (presentation === "dialog") {
    return createPortal(
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabelledBy ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledBy || undefined}
        data-sheet-presentation="dialog"
        tabIndex={-1}
        className="fixed inset-0 z-[240] flex items-center justify-center p-3 sm:p-6"
        style={{
          background: "rgba(4,8,18,0.45)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          touchAction: "manipulation",
        }}
        onPointerDown={onBackdropPointerDown}
      >
        <div
          data-sheet-panel="dialog"
          className="flex w-full max-w-[560px] flex-col bg-[rgb(var(--clr-surface-cont-highest))]"
          style={{
            height: "min(720px, calc(100dvh - 32px))",
            maxHeight: "calc(100dvh - 32px)",
            borderRadius: 28,
            boxShadow: shadow.elev2,
            overflow: "hidden",
            pointerEvents: "auto",
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="min-h-0 flex-1 overflow-y-auto px-6 py-6"
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehaviorY: "contain",
              touchAction: "pan-y",
            }}
          >
            {children}
          </div>
          {footer ? (
            <div className="shrink-0 border-t border-[rgba(var(--clr-outline-variant),0.55)] px-6 py-4">
              {footer}
            </div>
          ) : null}
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabelledBy ? undefined : ariaLabel}
      aria-labelledby={ariaLabelledBy || undefined}
      data-sheet-presentation="sheet"
      data-bottom-sheet="open"
      tabIndex={-1}
      className="fixed inset-x-0 bottom-0 z-[240] flex items-end justify-center px-0"
      style={{
        top: "calc(-1 * (env(safe-area-inset-top) + 32px))",
        minHeight: "calc(100dvh + env(safe-area-inset-top) + 32px)",
        paddingTop: "calc(max(16px, env(safe-area-inset-top)) + 32px)",
        background: "rgba(4,8,18,0.45)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        touchAction: "manipulation",
      }}
      onPointerDown={onBackdropPointerDown}
    >
      {/* Rail container */}
      <div
        className="w-full [--rail-pad:12px] md:[--rail-pad:16px] lg:[--rail-pad:16px]"
        style={{
          maxWidth: 720,
          marginLeft: "auto",
          marginRight: "auto",
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Allow wider rail at lg */}
        <div className="w-full" style={{ maxWidth: "min(1008px, 100vw)" }}>
          {/* Outer gutters (outside panel) */}
          <div className="w-full px-[var(--rail-pad)] pb-[env(safe-area-inset-bottom)] lg:px-4">
            {/* Panel */}
            <div
              className="bg-[rgb(var(--clr-surface-cont-highest))] flex min-h-[400px] w-full flex-col md:min-h-0"
              style={{
                boxShadow: shadow.elev2,
                borderTopLeftRadius: "var(--afrimed-sheet-radius)",
                borderTopRightRadius: "var(--afrimed-sheet-radius)",
                maxHeight: "calc(100vh - env(safe-area-inset-top) - 24px)",
                overflow: "hidden",
                pointerEvents: "auto",
              }}
            >
              {/* Drag handle (NOT padded by content padding) */}
              <div className="shrink-0 pt-3 pb-2 flex justify-center">
                <div className="h-1.5 w-12 rounded-full bg-[rgba(91,107,134,0.25)]" />
              </div>

              {/* ✅ SSOT content padding for ALL sheets */}
              <div
                className="min-h-0 flex-1 overflow-y-auto px-[var(--rail-pad)] pb-[max(20px,env(safe-area-inset-bottom))]"
                style={{
                  WebkitOverflowScrolling: "touch",
                  overscrollBehaviorY: "contain",
                  touchAction: "pan-y",
                }}
              >
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
